import { useLocation, Link } from 'react-router-dom';
import { Bell, Search, Sun, Moon, SunMoon, ChevronRight } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { NAV_GROUPS } from './Sidebar';

function useBreadcrumb() {
  const location = useLocation();
  const path = location.pathname;

  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const base = item.path.split('?')[0];
      if (path === base) {
        return { group: group.label, page: item.label };
      }
    }
  }
  return null;
}

const THEME_CYCLE = { auto: 'light', light: 'dark', dark: 'auto' };
const THEME_ICONS = {
  auto: SunMoon,
  light: Sun,
  dark: Moon,
};

export default function TopBar({ collapsed, onSearchOpen }) {
  const { themeMode, setThemeMode, isAuto, dark } = useTheme();
  const { user } = useAuth();
  const breadcrumb = useBreadcrumb();

  const ThemeIcon = THEME_ICONS[themeMode];
  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <header
      className={`
        fixed top-0 right-0 z-20 h-14 flex items-center gap-3 px-4
        bg-white/80 dark:bg-stone-900/80 backdrop-blur-sm
        border-b border-stone-200 dark:border-stone-800
        transition-all duration-200
        ${collapsed ? 'md:left-16 left-0' : 'md:left-60 left-0'}
      `}
    >
      {/* Breadcrumb — desktop only */}
      <div className="flex-1 hidden md:flex items-center gap-1.5 text-sm">
        {breadcrumb && (
          <>
            <span className="text-stone-400 dark:text-stone-500 font-medium capitalize">
              {breadcrumb.group.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}
            </span>
            <ChevronRight size={13} className="text-stone-300 dark:text-stone-600" />
            <span className="text-stone-700 dark:text-stone-200 font-semibold">
              {breadcrumb.page}
            </span>
          </>
        )}
      </div>

      <span className="flex-1 md:hidden" />

      {/* Cmd+K trigger — desktop only */}
      <button
        onClick={onSearchOpen}
        className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg
          bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700
          text-stone-500 dark:text-stone-400 text-sm transition-colors"
      >
        <Search size={13} />
        <span>Search</span>
        <kbd className="text-[10px] bg-stone-200 dark:bg-stone-700 px-1.5 py-0.5 rounded font-mono">⌘K</kbd>
      </button>

      {/* Bell */}
      <button className="relative p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors text-stone-500 dark:text-stone-400">
        <Bell size={17} />
      </button>

      {/* 3-way theme toggle */}
      <button
        onClick={() => setThemeMode(THEME_CYCLE[themeMode])}
        title={`Theme: ${themeMode}${themeMode === 'auto' && dark ? ' (night)' : ''}`}
        className={`p-2 rounded-lg transition-colors
          ${themeMode === 'auto' && dark
            ? 'text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40'
            : 'text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800'
          }`}
      >
        <ThemeIcon size={17} />
      </button>

      {/* Avatar → Profile */}
      <Link
        to="/profile"
        className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center
          text-white text-xs font-bold hover:bg-orange-600 transition-colors shrink-0"
      >
        {initials}
      </Link>
    </header>
  );
}
